import { formattedCurrency, withStyles } from 'vopay';
import { FunctionComponent, useRef, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import LottieFile from '../../components/LottieFile';
import LottieCollection from '../../LottieFiles/Collections.json';
import ExpandCard from '../../components/ExpandCard';
import type { LottieRefCurrentProps } from 'lottie-react';
import styles from './InteractiveInfo.module.scss';
import { AccountLimits } from '@/hooks/useApi/useFetchAccountLimits';
import Skeleton from '../../widgets/Skeleton';

import { Title } from 'UI';

const cn = withStyles(styles);

type TotalPaymentsCardProps = {
  loading: boolean;
  currency: string;
  accountLimits: AccountLimits | null;
};

type FC = FunctionComponent<TotalPaymentsCardProps>;

const TotalCollectionsCard: FC = ({ loading, currency, accountLimits }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'HomepageRevamp' });
  const { onSubNavClick } = useNavMenu();
  const { permissions } = usePermissions();
  const collectionRef = useRef<LottieRefCurrentProps>(null);

  const handleLottieFileHover = (
    ref: RefObject<LottieRefCurrentProps | null>,
    isEnter: boolean,
    frameRange: [number, number] = [0, 45],
  ) => {
    if (!ref.current) return;

    ref.current.pause();
    const [start, end] = frameRange;

    if (isEnter) {
      ref.current.setDirection(1);
      ref.current.playSegments([start, end], true);
    } else {
      ref.current.setDirection(-1);
      ref.current.playSegments([end, start], true);
    }
  };

  const parseAmount = (value: string | undefined | null): number => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  return (
    <ExpandCard
      align="end"
      disabled={loading}
      buttonText={t('Widgets.InteractiveInfo.openSingleCollection')}
      showButton={permissions.CreateCollections}
      onMouseOver={() => handleLottieFileHover(collectionRef, true)}
      onMouseOut={() => handleLottieFileHover(collectionRef, false)}
      onNavigateClick={() => onSubNavClick('singleCollection', '/single-collection', t('collection'))}
      top={
        <div className={cn('totalBalanceTopContainer')}>
          <div className={cn('totalBalanceTop')}>
            <Title level="6" emphasized={false} className={cn('totalCardLabel')}>
              {t('Widgets.InteractiveInfo.totalMonthlyCollections')}
            </Title>

            {loading ? (
              <Title level="3" className={cn('totalCardAmount')}>
                <Skeleton />
              </Title>
            ) : accountLimits ? (
              <div className={cn('totalCardCurrency')}>
                <Title level="5" emphasized={false}>
                  {currency}
                </Title>

                <Title level="3" className={cn('totalCardAmount')}>
                  {formattedCurrency(
                    String(
                      parseAmount(accountLimits.Inbound?.Limits?.Monthly ?? 0) -
                        parseAmount(accountLimits.Inbound?.RemainingLimits?.Monthly ?? 0),
                    ),
                  )}
                </Title>
              </div>
            ) : null}
          </div>

          <LottieFile animationData={LottieCollection} lottieRef={collectionRef} />
        </div>
      }
      contents={
        <div className={cn('totalBalanceContents')}>
          {accountLimits ? (
            <>
              <div className={cn('totalBalanceCard')}>
                <div>{t('Widgets.InteractiveInfo.dailyCollections')}</div>
                <div>
                  {currency}{' '}
                  {formattedCurrency(
                    String(
                      parseAmount(accountLimits?.Inbound?.Limits?.Daily ?? 0) -
                        parseAmount(accountLimits?.Inbound?.RemainingLimits?.Daily ?? 0),
                    ),
                  )}
                </div>
              </div>

              <div className={cn('totalBalanceCard')}>
                <div>{t('Widgets.InteractiveInfo.weeklyCollections')}</div>
                <div>
                  {currency}{' '}
                  {formattedCurrency(
                    String(
                      parseAmount(accountLimits?.Inbound?.Limits?.Weekly ?? 0) -
                        parseAmount(accountLimits?.Inbound?.RemainingLimits?.Weekly ?? 0),
                    ),
                  )}
                </div>
              </div>

              <div className={cn('totalBalanceCard')}>
                <div>{t('Widgets.InteractiveInfo.monthlyCollections')}</div>
                <div>
                  {currency}{' '}
                  {formattedCurrency(
                    String(
                      parseAmount(accountLimits?.Inbound?.Limits?.Monthly ?? 0) -
                        parseAmount(accountLimits?.Inbound?.RemainingLimits?.Monthly ?? 0),
                    ),
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      }
    />
  );
};

export default TotalCollectionsCard;
